import { sanitizeListboxScalarValue } from '@/common/utils/listboxScalarSanitize'
import {
  listboxOptionValueFromRow,
  resolveListboxCodeFromPageFields,
  resolveListboxSelectedTextFromPageFields,
} from '@/common/utils/listboxTableResolve'

export function extractListboxScalarValue(entry: unknown): unknown {
  let current: unknown = entry
  let depth = 0
  while (
    current != null &&
    typeof current === 'object' &&
    !Array.isArray(current) &&
    'value' in (current as Record<string, unknown>) &&
    depth < 8
  ) {
    current = (current as { value: unknown }).value
    depth += 1
  }
  return sanitizeListboxScalarValue(current)
}

export function isListboxEmptyValue(value: unknown): boolean {
  return value === '' || value === null || value === undefined
}

export function resolveListboxCatalogFirstValue(
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
): unknown {
  if (!pageFields || !fieldName) return null
  const rows =
    pageFields[`${fieldName}__listbox`]?.value?.rows ??
    pageFields[`${fieldName}__items`]?.value?.rows ??
    pageFields[fieldName]?.value?.rows
  if (!Array.isArray(rows) || rows.length === 0) return null
  const first = listboxOptionValueFromRow(rows[0])
  return first === '' ? null : first
}

/** Пустое значение → первый пункт каталога. */
export function normalizeListboxFieldValue(
  value: unknown,
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
): unknown {
  if (!isListboxEmptyValue(value)) return value
  const fallback = resolveListboxCatalogFirstValue(pageFields, fieldName)
  return fallback ?? value
}

export function resolveListboxFieldValueFromPageFields(
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
): unknown {
  if (!pageFields || !fieldName) return null
  const entry = pageFields[fieldName]
  if (entry === undefined) {
    return normalizeListboxFieldValue(null, pageFields, fieldName)
  }
  const scalar = extractListboxScalarValue(entry)
  return normalizeListboxFieldValue(scalar, pageFields, fieldName)
}

export function listboxFieldValuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  return String(a) === String(b)
}

/** Значение для UI: store/raw + fallback на pageFields. */
export function resolveListboxDisplayValue(
  rawValue: unknown,
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
): unknown {
  const fromPage = resolveListboxFieldValueFromPageFields(pageFields, fieldName)
  const scalar = extractListboxScalarValue(rawValue)

  if (!isListboxEmptyValue(scalar)) {
    return normalizeListboxFieldValue(scalar, pageFields, fieldName)
  }
  if (fromPage !== null && fromPage !== undefined) {
    return fromPage
  }
  return normalizeListboxFieldValue(scalar ?? null, pageFields, fieldName)
}

/** Подпись выбранного ListBox для UI: __text → description из __listbox по code. */
export function resolveListboxDisplayLabelFromPageFields(
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
  cellValue?: unknown,
): string | undefined {
  if (!fieldName) return undefined
  const fromText = resolveListboxSelectedTextFromPageFields(pageFields, fieldName)
  if (fromText) return fromText
  const value =
    cellValue !== undefined
      ? sanitizeListboxScalarValue(cellValue)
      : resolveListboxFieldValueFromPageFields(pageFields, fieldName)
  return resolveListboxCodeFromPageFields(pageFields, fieldName, value)
}
